(() => {
  window.DigitalizModules = window.DigitalizModules || {};
  if (window.DigitalizModules.mainProductGallery) return;
  window.DigitalizModules.mainProductGallery = true;

  // Layout constants — these MUST stay in sync with component-product-gallery.css.
  // Desktop: thumb column width = calc(25% - 8.75px), 15px main<->thumb gap,
  // 10px thumb gap, 3 per view → the 3 squared thumbs equal the main square height.
  const DESKTOP_MIN = 992;
  const THUMB_GAP_DESKTOP = 10;
  const THUMB_GAP_MOBILE = 5;
  // Max wait for the active slide's image before the gallery is revealed anyway.
  const REVEAL_TIMEOUT_MS = 2000;

  const galleryState = new WeakMap();

  const isDesktop = () => window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`).matches;

  function toNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function getSlides(root) {
    const variantSync = window.DigitalizProductGalleryVariantSync;
    if (variantSync?.getSlides) return variantSync.getSlides(root);
    return [...root.querySelectorAll('[data-product-gallery-slide]')];
  }

  function getLogicalIndex(index, total) {
    if (!total) return 0;
    return ((Number(index) % total) + total) % total;
  }

  function getThumbsPerView(root) {
    return Math.max(1, toNumber(root.dataset.thumbsPerView, 3));
  }

  function shouldEnableMainSlider(root) {
    return root.dataset.mainSlider === 'true' && typeof window.Splide === 'function';
  }

  function shouldEnableThumbSlider(root) {
    return root.dataset.thumbsSlider === 'true' && typeof window.Splide === 'function';
  }

  function setActiveThumb(root, index) {
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    if (!thumbs.length) return;

    const logicalIndex = getLogicalIndex(index, thumbs.length);

    thumbs.forEach((thumb, fallbackIndex) => {
      const thumbIndex = Number(thumb.dataset.galleryIndex);
      const compareIndex = Number.isFinite(thumbIndex) ? thumbIndex : fallbackIndex;
      const isActive = compareIndex === logicalIndex;
      thumb.classList.toggle('active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    const state = galleryState.get(root);
    if (state?.thumbs && !state.syncingViaSplide) {
      state.thumbs.go(logicalIndex);
    }
  }

  function getMainOptions(root) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const loop = root.dataset.loop === 'true';
    return {
      type: loop ? 'loop' : 'slide',
      perPage: 1,
      gap: 0,
      arrows: root.dataset.showArrows === 'true',
      pagination: false,
      drag: true,
      speed: reducedMotion ? 0 : 300,
      rewind: !loop,
      autoplay: root.dataset.autoplay === 'true',
      interval: Math.max(1000, toNumber(root.dataset.autoplayDelay, 4000)),
      pauseOnHover: true,
      pauseOnFocus: true,
      updateOnMove: true
    };
  }

  function mainAreaHeight(root) {
    const mainArea = root.querySelector('.gallery-main-area');
    if (!mainArea) return 0;
    return Math.round(mainArea.getBoundingClientRect().height);
  }

  // The main image is a 1:1 square sized purely by CSS. On desktop the vertical
  // (ttb) thumb slider needs an explicit pixel height: hand it the square's height
  // so its 3 thumbs line up exactly with the image. On mobile the thumbs are a
  // horizontal strip and need no height.
  function syncThumbsLayout(root, state) {
    const thumbsEl = root.querySelector('[data-gallery-thumbs]');
    if (!thumbsEl) return;

    if (!isDesktop()) {
      thumbsEl.style.height = '';
      const track = thumbsEl.querySelector('.splide__track');
      if (track) track.style.height = '';
      return;
    }

    const height = mainAreaHeight(root);
    if (!height) return;

    thumbsEl.style.height = `${height}px`;
    if (state?.thumbs) {
      state.thumbs.options = { height };
      const track = thumbsEl.querySelector('.splide__track');
      if (track) track.style.height = `${height}px`;
      state.thumbs.emit('resize');
    }
  }

  function prepareThumbMarkup(root) {
    const thumbsRoot = root.querySelector('[data-gallery-thumbs]');
    const track = root.querySelector('.gallery-thumbs-track');
    const list = root.querySelector('.gallery-thumbs-list');
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];

    if (!thumbsRoot || !track || !list || !thumbs.length) return null;

    thumbsRoot.classList.add('splide');
    track.classList.add('splide__track');
    list.classList.add('splide__list');
    thumbs.forEach((thumb) => thumb.classList.add('splide__slide'));

    return { thumbsRoot, track, list, thumbs };
  }

  function cleanupThumbMarkup(root) {
    const thumbsRoot = root.querySelector('[data-gallery-thumbs]');
    const track = root.querySelector('.gallery-thumbs-track');
    const list = root.querySelector('.gallery-thumbs-list');
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];

    if (thumbsRoot) {
      thumbsRoot.classList.remove('splide');
      thumbsRoot.style.height = '';
    }
    if (track) {
      track.classList.remove('splide__track');
      track.style.height = '';
    }
    if (list) list.classList.remove('splide__list');
    thumbs.forEach((thumb) => thumb.classList.remove('splide__slide'));
  }

  function destroy(root) {
    const state = galleryState.get(root);
    if (state?.main) {
      state.main.destroy(true);
      state.main = null;
    }
    if (state?.thumbs) {
      state.thumbs.destroy(true);
      state.thumbs = null;
    }
    if (state) {
      state.syncingViaSplide = false;
      galleryState.set(root, state);
    }
    cleanupThumbMarkup(root);
  }

  function init(root) {
    const state = galleryState.get(root) || { main: null, thumbs: null, syncingViaSplide: false };
    galleryState.set(root, state);

    const useMain = shouldEnableMainSlider(root);
    const useThumbs = shouldEnableThumbSlider(root);

    root.classList.toggle('is-slider-active', useMain);
    root.classList.toggle('is-thumbs-slider-active', useThumbs);

    destroy(root);
    root.classList.remove('is-initialized');

    if (!useMain) {
      // Single image (or Splide unavailable): nothing to mount, just reveal.
      requestAnimationFrame(() => root.classList.add('is-initialized'));
      return;
    }

    const viewport = root.querySelector('[data-product-gallery-viewport]');
    if (!viewport) return;

    if (useThumbs) {
      const prepared = prepareThumbMarkup(root);
      if (prepared) {
        const desktop = isDesktop();
        state.thumbs = new window.Splide(prepared.thumbsRoot, {
          direction: desktop ? 'ttb' : 'ltr',
          type: 'slide',
          rewind: false,
          perPage: getThumbsPerView(root),
          perMove: 1,
          gap: `${desktop ? THUMB_GAP_DESKTOP : THUMB_GAP_MOBILE}px`,
          arrows: false,
          pagination: false,
          isNavigation: true,
          drag: true,
          updateOnMove: true,
          ...(desktop ? { height: mainAreaHeight(root) } : {})
        });
      }
    }

    state.main = new window.Splide(viewport, getMainOptions(root));

    if (state.main && state.thumbs) {
      state.syncingViaSplide = true;
      state.main.sync(state.thumbs);
    }

    state.main.on('mounted move moved', () => {
      // Expose the current slide index so interactions (e.g. the zoom button) can
      // open the lightbox at the slide actually on screen.
      root.dataset.activeIndex = String(state.main.index);
      setActiveThumb(root, state.main.index);
    });

    // Splide v4 sync: main must mount before thumbs (isNavigation timing).
    state.main.mount();
    if (state.thumbs) state.thumbs.mount();

    setActiveThumb(root, state.main.index);
    syncThumbsLayout(root, state);

    // Reveal once the active slide's image is in. Use the real (non-clone)
    // slide: in a loop slider the first slide in the DOM is a clone, and a
    // clone's lazy image may never load when the gallery opens on a later
    // slide (variant preselected via ?variant=), which left the loader up
    // forever. The timeout is a safety net so the loader can never get stuck.
    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      root.classList.add('is-initialized');
      syncThumbsLayout(root, state);
    };
    const realSlides = viewport.querySelectorAll('[data-product-gallery-slide]:not(.splide__slide--clone)');
    const activeSlide = realSlides[state.main.index] || realSlides[0];
    const activeImg = activeSlide ? activeSlide.querySelector('img') : null;
    if (activeImg && !activeImg.complete) {
      activeImg.addEventListener('load', reveal, { once: true });
      activeImg.addEventListener('error', reveal, { once: true });
      window.setTimeout(reveal, REVEAL_TIMEOUT_MS);
    } else {
      reveal();
    }

    galleryState.set(root, state);
  }

  function goToSlideIndex(root, index) {
    const slides = getSlides(root);
    if (!slides.length) return;

    const logicalIndex = getLogicalIndex(index, slides.length);
    const state = galleryState.get(root);

    if (state?.main) state.main.go(logicalIndex);
    setActiveThumb(root, logicalIndex);
  }

  function syncFromCurrentVariant(root) {
    const sectionId = root.dataset.sectionId;
    const variantSync = window.DigitalizProductGalleryVariantSync;
    if (!sectionId || !variantSync?.getSlideIndexFromMap) return;

    const formContainer = document.querySelector(`[data-product-form][data-section-id="${sectionId}"]`);
    const variantId = Number(formContainer?.querySelector('[data-variant-id-input]')?.value);
    if (!Number.isFinite(variantId) || variantId <= 0) return;

    const mappedIndex = variantSync.getSlideIndexFromMap(root, variantId);
    if (mappedIndex >= 0) goToSlideIndex(root, mappedIndex);
  }

  function bindThumbs(root) {
    root.querySelectorAll('[data-gallery-thumb]').forEach((thumb, fallbackIndex) => {
      if (thumb.dataset.bound === 'true') return;
      thumb.dataset.bound = 'true';
      thumb.addEventListener('click', () => {
        const logicalIndex = Number(thumb.dataset.galleryIndex);
        goToSlideIndex(root, Number.isFinite(logicalIndex) ? logicalIndex : fallbackIndex);
      });
    });
  }

  function bindVariantSync(root) {
    if (root.dataset.variantSyncBound === 'true') return;
    root.dataset.variantSyncBound = 'true';

    const sectionId = root.dataset.sectionId;
    const variantSync = window.DigitalizProductGalleryVariantSync;

    document.addEventListener('digitaliz:variant:change', (event) => {
      const eventSectionId = event.detail?.container?.dataset?.sectionId;
      if (!sectionId || !eventSectionId || sectionId !== eventSectionId) return;

      const variant = event.detail?.variant;
      if (!variant || !variantSync?.getSlideIndexFromVariant) return;

      const index = variantSync.getSlideIndexFromVariant(root, variant);
      if (index < 0) return;
      goToSlideIndex(root, index);
    });
  }

  function initRoot(root) {
    if (root.dataset.galleryBound === 'true') return;
    root.dataset.galleryBound = 'true';

    galleryState.set(root, { main: null, thumbs: null, syncingViaSplide: false });
    bindThumbs(root);
    bindVariantSync(root);
    init(root);

    const interactions = window.DigitalizProductGalleryInteractions;
    if (interactions?.bind) interactions.bind(root, { isDesktop });

    syncFromCurrentVariant(root);
  }

  function initAll() {
    document.querySelectorAll('[data-main-product-gallery]').forEach(initRoot);
  }

  let lastDesktop = null;
  let resizeTimer = null;

  function handleResize() {
    const nowDesktop = isDesktop();
    const crossedBreakpoint = lastDesktop !== null && nowDesktop !== lastDesktop;
    lastDesktop = nowDesktop;

    document.querySelectorAll('[data-main-product-gallery]').forEach((root) => {
      if (crossedBreakpoint) {
        // Thumb direction (ttb <-> ltr) changes — rebuild.
        init(root);
        const state = galleryState.get(root);
        setActiveThumb(root, state?.main ? state.main.index : 0);
      } else {
        // Same orientation — just re-fit the thumb column to the square height.
        syncThumbsLayout(root, galleryState.get(root));
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    lastDesktop = isDesktop();
    initAll();
  });
  document.addEventListener('shopify:section:load', initAll);
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(handleResize, 150);
  });
})();
